import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { MoneyInput } from '@/components/ui/money-input';
import { RadioGroup } from '@/components/ui/radio-group';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ToastProvider';
import { errorCode, errorMessage } from '@/lib/errors';
import {
  plannedOrToday,
  preferredSettleAccount,
  queueItemDefaultAccount,
  useOperationId,
  useSettleFinanceExpected,
} from '@/lib/finance';
import type { ExtraLine } from '@/lib/financeMath';
import { formatMinor, fromMinor, toMinor } from '@/lib/money';
import { isHighlighted, needsSecondPress, settleOutcome } from '@/lib/planDeviation';
import {
  canSubmitSingle,
  draftLineInputs,
  draftReady,
  feeArrivedMinor,
  feeGapMinor,
  liveLines,
  sameContext,
  staleFieldError,
  singleBankMinor,
  singleSettleMinor,
  targetComparison,
  type DraftContext,
  type DraftEvent,
  type TargetKey,
} from '@/lib/settleDraft';
import { AttachmentList } from './AttachmentList';
import { ReceiptUploadButton } from './ReceiptUploadButton';
import { newPlanText, offPlanText, outcomeText } from './planOutcomeText';
import { DraftConflictPanel, DraftLinesEditor, DraftStalePanel, DraftSuggestionInfo } from './SettleDraftPanels';
import { useConfirmTwice } from './useConfirmTwice';
import { useSettleDraft } from './useSettleDraft';
import type {
  FinanceAccount,
  FinanceCategory,
  FinanceQueueItem,
  FinanceRule,
  FinanceSettleDependent,
  FinanceSettleRequest,
} from '@/api/generated/types.gen';

// OH-205 §3.3: «write off» is no longer a choice of the window — closing at what was paid means the rest is not
// awaited; the server keeps the mode for older clients.
type RemainderMode = Exclude<NonNullable<FinanceSettleRequest['remainder']>, 'write_off'>;
// OH-193: the fourth answer — the bank kept the difference as a fee. Not a
// wire mode: it sends no remainder; the target settles in full and the
// server's shortfall line closes the gap (spec §4.4 p.6).
type Mode = RemainderMode | 'fee';

const REMAINDER_OPTIONS: { value: RemainderMode; label: string }[] = [
  { value: 'restate', label: 'The obligation was actually this amount' },
  { value: 'partial', label: 'Part paid — keep the rest in the queue' },
];
const FEE_LABEL = 'The bank kept the difference as a fee';
const LINE_KINDS: readonly ExtraLine['kind'][] = ['expense', 'income', 'withheld'];
const DEPENDENTS_HINT = "Settle the linked payments separately — they follow this row's new plan";

// What the window compares for one row: the payment against its plan, or the explicit settled_minor against the remaining.
type Comparison = NonNullable<ReturnType<typeof targetComparison>>;

export interface SettleDialogProps {
  item: FinanceQueueItem | null;
  accounts: readonly FinanceAccount[];
  categories: readonly FinanceCategory[];
  /** The rules, for the account a rule's row starts on (OH-203); omitted — the category's default decides. */
  rules?: readonly FinanceRule[];
  /**
   * The queue's OTHER currently-loaded rows, offered as co-settled
   * dependents (spec §8.2). There is no "children of this expected row"
   * endpoint (only the queue listing and get-by-id exist — api/openapi.yaml
   * has no such filter), so this is necessarily scoped to what the page
   * already fetched, not a full search. Optional — omit to hide the
   * dependents section entirely (e.g. a caller with no sibling rows handy).
   */
  siblingItems?: readonly FinanceQueueItem[];
  businessTz?: string;
  onClose: () => void;
}

/**
 * Confirm one queue row (spec §8.2). Split in two so the operation_id's
 * per-row contract is provable: SettleDialog stays mounted for the whole
 * page (toggled by `item`, not by mount/unmount — the queue page renders it
 * once, unconditionally) and owns ONLY useOperationId(item's own id), which
 * is exactly the hook's "toggled by prop" contract (lib/finance.ts). Every
 * other piece of form state (amount, extras, receipts, remainder…) lives in
 * the inner SettleForm, keyed by the row id — switching rows remounts it,
 * so a half-filled amount or an uploaded receipt from row A can never leak
 * into row B's request. Without the split, one component owning both the id
 * AND the field state would have to manually reset a dozen fields on every
 * row switch, and forgetting one silently mixes two rows' data.
 */
export function SettleDialog({
  item,
  accounts,
  categories,
  rules,
  siblingItems,
  businessTz,
  onClose,
}: SettleDialogProps) {
  const expectedId = item?.target.expected_id;
  const [operationId] = useOperationId(expectedId);
  return (
    <Dialog
      open={!!item}
      onClose={onClose}
      side
      title="Confirm payment"
      description={
        item ? `${formatMinor(item.amount_minor - item.settled_minor, item.currency)} expected` : undefined
      }
    >
      {item &&
        // The form's hooks (the second press among them) need a row to settle: without an id there is no form.
        (expectedId ? (
          <SettleForm
            key={expectedId}
            item={item}
            expectedId={expectedId}
            operationId={operationId}
            accounts={accounts}
            categories={categories}
            rules={rules ?? []}
            siblingItems={siblingItems ?? []}
            businessTz={businessTz}
            onClose={onClose}
          />
        ) : (
          <p role="alert" className="text-sm text-destructive">
            This row has no expected-payment id and cannot be confirmed here.
          </p>
        ))}
    </Dialog>
  );
}

interface SettleFormProps {
  item: FinanceQueueItem;
  expectedId: string;
  operationId: string;
  accounts: readonly FinanceAccount[];
  categories: readonly FinanceCategory[];
  rules: readonly FinanceRule[];
  siblingItems: readonly FinanceQueueItem[];
  businessTz?: string;
  onClose: () => void;
}

// The article a line may carry has to agree with its kind (movements.go, the
// same rule MovementForm's LineFields enforces) — withheld may carry any
// active article, income/expense may not cross into the opposite kind.
function categoriesFor(kind: ExtraLine['kind'], active: readonly FinanceCategory[]) {
  return active.filter((c) => {
    if (kind === 'income') return c.kind !== 'expense';
    if (kind === 'expense') return c.kind !== 'income';
    return true;
  });
}

function SettleForm({
  item,
  expectedId,
  operationId,
  accounts,
  categories,
  rules,
  siblingItems,
  businessTz,
  onClose,
}: SettleFormProps) {
  const { toast } = useToast();
  const settle = useSettleFinanceExpected();
  const activeCategories = categories.filter((c) => c.is_active);

  // OH-203: the account the row is set up to go through (its rule's, else its category's).
  const preferredAccount = preferredSettleAccount({
    accounts,
    currency: item.currency,
    ...queueItemDefaultAccount(item, rules, categories),
  });
  const [accountId, setAccountId] = useState(preferredAccount?.id ?? '');
  const account = accounts.find((a) => a.id === accountId);
  const [currency, setCurrency] = useState(
    preferredAccount?.currencies.includes(item.currency) ? item.currency : (preferredAccount?.currencies[0] ?? item.currency),
  );
  // OH-201: the day the payment was planned for, once it has come.
  const [occurredOn, setOccurredOn] = useState(() => plannedOrToday(item.due_date, businessTz));
  const [settledOverrideMajor, setSettledOverrideMajor] = useState('');
  // OH-205 §3.1/§3.3: below plan the row closes at what was paid unless the operator picks otherwise
  const [remainder, setRemainder] = useState<RemainderMode>('restate');
  // OH-193 [PR-03]: the bank amount and the extra lines live in the settle
  // draft, filled from the preview and sent with its base as `expect`; the
  // remainder mode, settled_minor and the dependents stay this form's own.
  // OH-201: the form counts from the payment amount — the bank amount follows
  // it and the lines — until the operator types the bank amount itself.
  const key: TargetKey = `expected:${expectedId}`;
  const context = useMemo<DraftContext | null>(
    () => (accountId && currency && occurredOn ? { accountId, currency, occurredOn, targets: [key] } : null),
    [key, accountId, currency, occurredOn],
  );
  const { state, dispatch, preview: suggestionQuery, refetchAfterConflict } = useSettleDraft({
    context,
    initialSendsBank: false,
    initialAnchor: 'settle',
    bankBeforeOperatorLines: true,
  });
  const [feeMode, setFeeMode] = useState(false);
  const [dependentIds, setDependentIds] = useState<ReadonlySet<string>>(new Set());
  const [receipts, setReceipts] = useState<{ id: string; name: string }[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Set only by the server's remainder_required rejection. The window reads the
  // row the way the server does (planDeviation), so this is the server
  // disagreeing with that reading — the question is shown then and stays shown,
  // which turns the picker on for the resubmit the operator is about to make.
  // Holds the server's OWN sentence (errorMessage(err), via settle.go's wrap →
  // finance_errors.go's tail()) verbatim — shown as the "amounts the server
  // returned" rather than reparsed, since regexing numbers out of a message
  // meant for humans is the same fragility a wire contract exists to avoid.
  const [serverVerdict, setServerVerdict] = useState<string | null>(null);

  // Same counterparty as the parent (spec §8.2 — otherwise "settled
  // separately, only its sum is recalculated") and the SAME currency as
  // this payment: FinanceSettleDependent.amount_minor is in the payment
  // currency, and without an FX rate lookup here there is no honest way to
  // convert a different-currency dependent's own remaining plan into it.
  const eligibleDependents = siblingItems.filter(
    (it) =>
      it.target.expected_id &&
      it.target.expected_id !== expectedId &&
      it.expected?.parent_expected_id === expectedId &&
      it.counterparty_id === item.counterparty_id &&
      it.currency === currency,
  );
  const tickedDependents = eligibleDependents.filter(
    (it) => it.target.expected_id && dependentIds.has(it.target.expected_id),
  );

  const sameCurrency = currency === item.currency;
  // Hidden and not sent while «fee» is on [PR3-02].
  const settledOverrideMinor = sameCurrency || feeMode ? null : toMinor(settledOverrideMajor, item.currency);
  const remainingMinor = state.base?.expect.targets[0]?.remaining_minor ?? item.amount_minor - item.settled_minor;

  // OH-205 (spec §3.1, §3.7, §4; plan decisions 7, 10, 11): what Confirm would do with the row if exactly `set` of its
  // dependents went along. The payment is compared with its own plan (the dependents' share is out of the bank when
  // counting from it); with an explicit settled_minor that is compared with the remaining too [PL-03] and DECIDES the
  // outcome [PL-R2-01] — the server takes it before the old tolerance and the band. The mode is the one the body
  // carries: the operator's answer while the question shows, none otherwise — which the server reads as restate.
  // The question shows below plan, after the server's own «remainder required», and in the fee mode (where it keeps
  // the way back). Nothing is compared while the draft is in a conflict, stale or still empty: its plan and its base are
  // not one answer. A CLEAN draft is compared even before the answer to the request shape it has just switched to
  // lands — the plan does not depend on the shape (plan decision 5), and `draftReady` would take the question away from
  // the radio the operator has just clicked on leaving the fee mode.
  // A payment of zero or below — or a settled amount of zero or below beside it — is compared with nothing: no outcome
  // line, no second press. The question shows only where it shows without a comparison: on the server's own verdict,
  // and in the fee mode, where it is the way out of the mode. The problem list names every such case and keeps Confirm
  // closed (the payment amount, the settled amount; in the fee mode what arrived), because the comparison used to be
  // the only thing that asked a second press for a body the lines still make add up.
  const comparable = state.status === 'clean';
  function verdictFor(set: readonly FinanceQueueItem[]) {
    let pay: Comparison | null = null;
    let override: Comparison | null = null;
    if (comparable) {
      const paid = feeMode ? feeArrivedMinor(state) : singleSettleMinor(state, set.map((d) => d.amount_minor - d.settled_minor));
      const nothingToCompare = (paid !== null && paid <= 0) || (settledOverrideMinor !== null && settledOverrideMinor <= 0);
      if (!nothingToCompare) {
        pay = paid === null ? null : targetComparison(state, key, paid);
        if (settledOverrideMinor !== null) {
          override = {
            paidMinor: settledOverrideMinor,
            plannedMinor: remainingMinor,
            crossCurrency: false,
            atPlan: settledOverrideMinor === remainingMinor,
            remainingMinor,
            settledMinor: settledOverrideMinor,
          };
        }
      }
    }
    const deciding = override ?? pay;
    const belowPlan = deciding !== null && isHighlighted(deciding) && deciding.paidMinor < deciding.plannedMinor;
    const ask = belowPlan || serverVerdict !== null || feeMode;
    const mode: Mode = feeMode ? 'fee' : ask ? remainder : 'restate';
    return { pay, override, deciding, ask, outcome: deciding ? settleOutcome({ ...deciding, mode, settledSoFarMinor: item.settled_minor }) : null };
  }

  // §3.7: a linked payment goes along only while the row stays at plan — the server refuses one beside a row that
  // restates off plan. Nothing to compare yet is no «restate».
  const allowedWith = (set: readonly FinanceQueueItem[]) => !feeMode && verdictFor(set).outcome?.kind !== 'restate';
  // The operator's ticks are kept, and what is shown and sent is derived from them: while the outcome does not allow
  // them they show unticked and disabled and go nowhere; when it allows them again — an amount typed on the way
  // passes through outcomes that do not — they are back, ticked, and in the body.
  const ticksAllowed = allowedWith(tickedDependents);
  // What is shown and sent, and what the outcome, the question and the body are all computed with.
  const selectedDependents = ticksAllowed ? tickedDependents : [];
  const verdict = verdictFor(selectedDependents);
  // A tick is enabled when the outcome allows it WITH the dependent: with a bank amount typed its share is in the bank,
  // and without it the parent looks overpaid (127.00 = 117.00 + 10.00) — a check without it would shut the tick for good.
  // And while the kept ticks do not fit, the one that fits ALONE is enabled too: a set that no longer fits (the bank was
  // retyped) must not lock out the dependent that does — hidden and disabled, the ticks would leave no way out but
  // retyping the amount back.
  const ticksWith = (d: FinanceQueueItem) => (tickedDependents.includes(d) ? tickedDependents : [...tickedDependents, d]);
  const dependentRows = eligibleDependents.map((d) => ({
    dependent: d,
    ticked: selectedDependents.includes(d),
    enabled: allowedWith(ticksWith(d)) || (!ticksAllowed && allowedWith([d])),
  }));
  // A click does what the box shows. Shown ticked: it unticks that one. Shown unticked: it ticks it — with the kept
  // ticks when the outcome allows the set, otherwise alone, replacing the ticks that are hidden. Never a blind toggle of
  // the kept state: a hidden kept tick that fits alone shows unticked, so a click on it means «tick».
  function toggleDependent(d: FinanceQueueItem) {
    const id = d.target.expected_id as string;
    if (selectedDependents.includes(d)) {
      setDependentIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      return;
    }
    const together = allowedWith(ticksWith(d));
    setDependentIds((prev) => (together ? new Set(prev).add(id) : new Set([id])));
  }
  const showRemainder = verdict.ask;

  const dependentsMinor = selectedDependents.map((d) => d.amount_minor - d.settled_minor);
  const settleMinor = singleSettleMinor(state, dependentsMinor);
  // What the request sends as amount_minor: typed, or derived from the payment amount (OH-201).
  const bankMinor = singleBankMinor(state, dependentsMinor);
  // The fee mode closes when what arrived and the shortfall line make the plan (OH-205 §6); null outside it.
  const feeGap = feeGapMinor(state);
  const countsFromPayment = state.anchor === 'settle';
  const targetMoney = state.targetAmounts[key];
  // The field the form does not count from shows the other one's result.
  const shown = (minor: number | null) => (minor !== null && currency ? fromMinor(minor, currency) : '');
  // Only an incoming target may book a shortfall as a bank fee, and not on an account with a fee
  // rule — the server suggests no shortfall there (OH-202); not together with dependents or an
  // explicit settled_minor in this version [PR3-02].
  const offerFee = item.direction === 'in' && !account?.fee_rule;
  const feeBlocked = selectedDependents.length > 0 || settledOverrideMinor !== null;

  // Keep / Apply / Reset settle the question the 409 message pointed at.
  const resolve = (e: DraftEvent) => {
    setError(null);
    dispatch(e);
  };

  function leaveFeeMode() {
    setFeeMode(false);
    dispatch({ type: 'requestShape', sendsBank: false });
  }

  function chooseMode(v: Mode) {
    if (v === 'fee') {
      setFeeMode(true);
      dispatch({ type: 'requestShape', sendsBank: true });
      return;
    }
    setRemainder(v);
    leaveFeeMode();
  }

  const problems: string[] = [];
  if (!accountId) problems.push('Choose an account.');
  // The fee mode compares what ARRIVED, and the «Payment amount» field shows it whichever way the form counts. At zero or
  // below it is compared with nothing (verdictFor), so it closes Confirm here: the shortfall line, edited to the plan, and
  // a line of the operator's own can still make the rest of the body add up.
  const feeArrived = feeMode ? feeArrivedMinor(state) : null;
  const arrivedNotPositive = feeArrived !== null && feeArrived <= 0;
  if (countsFromPayment) {
    // In the fee mode the settlement is what arrived plus the shortfall line: unknown when either is, but only
    // a missing «what arrived» is the payment field's problem — a line the draft cannot read is named below.
    const paymentUnknown = feeMode ? feeArrived === null : settleMinor === null;
    if (paymentUnknown || arrivedNotPositive || (settleMinor !== null && settleMinor <= 0)) {
      problems.push(`Enter the payment amount, in ${currency || 'the account currency'}.`);
    } else if (bankMinor !== null && bankMinor <= 0) {
      problems.push('The bank amount comes out at zero or below — check the lines.');
    }
  } else if (bankMinor === null || bankMinor <= 0) {
    problems.push(`Enter the amount that hit the account, in ${currency || 'the account currency'}.`);
  } else if (arrivedNotPositive || (settleMinor !== null && settleMinor <= 0)) {
    // Counting from the bank, the payment is derived from the bank amount and the lines (in the fee mode, what arrived
    // is the bank less the operator's own lines), so it can come out at zero or below with a bank amount above it.
    problems.push(`Enter the payment amount, in ${currency || 'the account currency'}.`);
  }
  // The server refuses a settled_minor of zero or below (422 «settled_minor must be > 0»); the window compares nothing.
  if (settledOverrideMinor !== null && settledOverrideMinor <= 0) problems.push('The settled amount must be above zero.');
  if (!occurredOn) problems.push('Choose the date the money moved.');
  const amountProblemLines: number[] = [];
  liveLines(state).forEach((l, i) => {
    const amt = currency ? toMinor(l.amount.major, currency) : null;
    if (amt === null || amt <= 0) {
      problems.push(`Line ${i + 1} needs an amount above zero.`);
      amountProblemLines.push(i);
    }
    if (!l.categoryId) problems.push(`Line ${i + 1} needs an article.`);
  });
  // The fee mode's settlement reads the shortfall line too. One the draft cannot read although its text parses (typed for
  // another account's currency) makes the settlement unknown all the same, and no problem above names it: name it here.
  // Only while no line already has an amount problem listed — such a line is named once, above.
  if (feeMode && settleMinor === null && feeArrivedMinor(state) !== null && amountProblemLines.length === 0) {
    const i = liveLines(state).findIndex((l) => l.source === 'shortfall');
    if (i >= 0) problems.push(`Line ${i + 1} can’t be read — enter its amount again.`);
  }
  // The draft follows the form's account, currency and date one effect late (useSettleDraft); in the render between, the
  // form shows the new one over the old draft's lines and base. Confirm waits for it, so that the body, the comparison
  // and the second press are always one draft's — by construction, though no input can reach that render.
  const caughtUp = context !== null && sameContext(context, state.context);
  const canConfirm =
    caughtUp &&
    problems.length === 0 &&
    canSubmitSingle(state, { isProjection: false, feeMode, dependentsMinor, settledOverride: settledOverrideMinor !== null });

  // The body Confirm would send, built once per render — the second press compares its fingerprint, submit sends it.
  // Only a form that can be confirmed has one; `canConfirm` needs a ready draft, so no fingerprint exists otherwise.
  const content: Omit<FinanceSettleRequest, 'operation_id'> | null =
    canConfirm && bankMinor !== null && state.base
      ? {
          account_id: accountId,
          currency,
          amount_minor: bankMinor,
          occurred_on: occurredOn,
          lines: draftLineInputs(state),
          dependents: selectedDependents.map(
            (d): FinanceSettleDependent => ({
              expected_id: d.target.expected_id as string,
              amount_minor: d.amount_minor - d.settled_minor,
            }),
          ),
          attachment_ids: receipts.map((r) => r.id),
          expect: state.base.expect,
          ...(showRemainder && !feeMode ? { remainder } : {}),
          ...(settledOverrideMinor !== null ? { settled_minor: settledOverrideMinor } : {}),
        }
      : null;
  // OH-205 §4: more than 5% off the plan — the payment against its plan, or the explicit settled_minor against the
  // remaining — and the first press only arms the button. `pressing` reads the amounts, the dependents and the lines,
  // which are in the body; the plans and rates they are compared with are a function of `expect`, which is in the body
  // too. A line's `source` is not in the body, and that cannot skip a press: swapping a shortfall line for an operator
  // line takes a remove and an add, two fingerprint changes.
  const pressing = [verdict.pay, verdict.override].filter((c): c is Comparison => c !== null && needsSecondPress(c));
  const twice = useConfirmTwice(content && JSON.stringify(content), pressing.length > 0);
  const outcomeLine = verdict.deciding && verdict.outcome
    ? outcomeText(verdict.deciding, verdict.outcome, verdict.deciding === verdict.override ? item.currency : currency, item.currency, currency)
    : null;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!content || !twice.press()) return;
    settle.mutate(
      { id: expectedId, body: { operation_id: operationId, ...content } },
      {
        onSuccess: () => {
          toast({ kind: 'success', title: 'Payment confirmed' });
          onClose();
        },
        onError: (err) => {
          // Same operation_id on the resubmit: it is still the same prop
          // value from the still-mounted SettleDialog (the operator picks a
          // mode and clicks Confirm again in this SAME form, never
          // closing/reopening it) — and the server-side claim for the
          // rejected attempt never became durable (its transaction rolled
          // back with the ErrRemainderRequired that raised it), so reusing
          // it here replays nothing and pays nothing twice.
          if (errorCode(err) === 'remainder_required') setServerVerdict(errorMessage(err));
          else if (errorCode(err) === 'target_changed') {
            // the plan or a rate moved since the suggestion: ask again, the draft goes into a conflict (S3)
            refetchAfterConflict();
            setError('The plan or an exchange rate changed since the suggestion — compare them below.');
          } else setError(errorMessage(err));
        },
      },
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <p className="rounded-[var(--radius-control)] bg-[var(--surface-sunken)] p-3 text-sm">
        {item.direction === 'in'
          ? 'Record money received into your account: the payment amount, any fees or deductions as lines, and the date from your bank statement.'
          : 'Record money paid out of your account: the payment amount, any bank fees or other costs on top as lines, and the date from your bank statement or receipt.'}
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <Select
          label="Account"
          value={accountId}
          options={accounts.map((a) => ({ value: a.id, label: a.name }))}
          onChange={(e) => {
            const next = accounts.find((a) => a.id === e.target.value);
            setAccountId(e.target.value);
            setCurrency(next?.currencies.includes(item.currency) ? item.currency : (next?.currencies[0] ?? ''));
            // An account with a fee rule has no fee mode (OH-202): the mode ends here as it does when any other
            // answer is picked — hiding the radio alone would leave the request asking for a shortfall.
            if (feeMode && next?.fee_rule) leaveFeeMode();
          }}
        />
        <Select
          label="Currency"
          value={currency}
          options={(account?.currencies ?? [item.currency]).map((c) => ({ value: c, label: c }))}
          onChange={(e) => setCurrency(e.target.value)}
        />
        <MoneyInput
          label="Payment amount"
          currency={currency || 'EUR'}
          // [PL-01] In the fee mode the field is always what arrived — never the settlement (what arrived
          // plus the shortfall): typing the number it shows must not change the money.
          value={countsFromPayment ? (targetMoney?.major ?? '') : shown(feeMode ? feeArrivedMinor(state) : settleMinor)}
          onChange={(major) => dispatch({ type: 'editSettle', major })}
          error={countsFromPayment && targetMoney ? staleFieldError(targetMoney, state.context.currency) : undefined}
          hint={
            feeMode
              ? 'What reached the account — a fee your bank charged separately goes in as a line below.'
              : item.direction === 'in'
                ? 'What the payer paid, before fees and deductions — those go in as lines below.'
                : 'What the payment itself came to, without bank fees — those go in as lines below.'
          }
          disabled={!context}
          required
        />
        <Input label="Date" type="date" value={occurredOn} onChange={(e) => setOccurredOn(e.target.value)} required />
      </div>

      <DraftConflictPanel state={state} dispatch={resolve} />
      <DraftStalePanel state={state} dispatch={resolve} />

      {!sameCurrency && !feeMode && (
        <MoneyInput
          label={`Settled amount (in ${item.currency})`}
          currency={item.currency}
          value={settledOverrideMajor}
          onChange={setSettledOverrideMajor}
          hint="Leave blank to use the NBS exchange rate on the payment date. Enter an amount only if you know exactly how much of the obligation this payment covers."
        />
      )}

      <DraftLinesEditor
        state={state}
        dispatch={dispatch}
        currency={currency}
        kinds={LINE_KINDS}
        articlesFor={(kind) => categoriesFor(kind, activeCategories)}
        allCategories={categories}
        disabled={!context}
      />

      {eligibleDependents.length > 0 && (
        <fieldset className="flex flex-col gap-2 border-0 p-0 m-0">
          <legend className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">
            Settle together with
          </legend>
          {dependentRows.map(({ dependent: d, ticked, enabled }) => {
            const id = d.target.expected_id as string;
            const remaining = d.amount_minor - d.settled_minor;
            // Full `categories`, not `activeCategories`: a dependent row can
            // carry an article that has since been deactivated (is_active is
            // an operator toggle on the category, not on rows already
            // classified with it) — falling back to the id only if the
            // lookup somehow misses is better than a blank label, never the
            // normal case.
            const categoryLabel = categories.find((c) => c.id === d.category_id)?.name ?? d.category_id;
            return (
              <Checkbox
                key={id}
                label={`${categoryLabel} — ${formatMinor(remaining, currency)}`}
                checked={ticked}
                disabled={!enabled}
                onChange={() => toggleDependent(d)}
              />
            );
          })}
          {/* Not in the fee mode: there the row stays at plan and the linked payments are shut out for their own reason. */}
          {!feeMode && dependentRows.some((r) => !r.enabled) && (
            <p className="text-xs text-muted" data-testid="dependents-hint">
              {DEPENDENTS_HINT}
            </p>
          )}
        </fieldset>
      )}

      <MoneyInput
        label="Bank amount"
        currency={currency || 'EUR'}
        value={countsFromPayment ? shown(bankMinor) : state.bank.major}
        onChange={(major) => dispatch({ type: 'editBank', major })}
        error={countsFromPayment ? undefined : staleFieldError(state.bank, state.context.currency)}
        hint={
          item.direction === 'in'
            ? 'Credited to the account: the payment less the deductions. Check it against your statement — change it and the payment amount follows.'
            : 'Debited from the account: the payment plus the lines. Check it against your statement — change it and the payment amount follows.'
        }
        disabled={!context}
        required
      />

      {settleMinor !== null && (
        <p className="text-sm text-muted" data-testid="settle-preview">
          Settles{' '}
          <span className="tabular-nums text-foreground">{formatMinor(settleMinor, currency)}</span>
          {sameCurrency && (
            <>
              {' '}
              of <span className="tabular-nums text-foreground">{formatMinor(remainingMinor, currency)}</span> remaining
              {settleMinor > remainingMinor && (
                <> — the plan will be restated to {newPlanText(item.settled_minor + settleMinor, item.settled_minor, currency)}</>
              )}
            </>
          )}
          {!sameCurrency &&
            (settledOverrideMinor !== null ? (
              <>
                {' '}
                — recorded as{' '}
                <span className="tabular-nums text-foreground">
                  {formatMinor(settledOverrideMinor, item.currency)}
                </span>{' '}
                against the {item.currency} obligation
              </>
            ) : (
              <> — converted to {item.currency} by the NBS rate of {occurredOn || 'the payment date'}</>
            ))}
        </p>
      )}
      {/* The fee mode closes only at a zero gap (OH-202, OH-205 §6): say by how much it is off, never just
          disable Confirm. Signed as the difference always was: plus — over the plan. Only for a draft that is
          ready: beside the conflict or the stale panel the gap is the new plan against old amounts. */}
      {feeMode && feeGap !== null && feeGap !== 0 && draftReady(state) && (
        <p className="text-sm text-[var(--ollio-gold-deep)]" data-testid="settle-difference">
          Off by {formatMinor(-feeGap, currency)}: what arrived and the shortfall line must add up to the plan.
        </p>
      )}
      {/* OH-205 §4: whenever the fact differs from the plan — what Confirm does about it, where the amounts are. */}
      {outcomeLine && (
        <p className="text-sm font-medium text-[var(--ollio-gold-deep)]" data-testid="settle-outcome">
          {outcomeLine}
        </p>
      )}

      {showRemainder && (
        <fieldset className="flex flex-col gap-2 border-0 p-0 m-0">
          <legend className="text-sm font-semibold text-[var(--text-heading)]">
            Below plan — what about the rest?
          </legend>
          {/* The server's own sentence (settle.go's wrap → tail()), shown
              verbatim — this IS "the amounts the server returned": the
              cross-currency settled amount only the server can compute, not
              a client-side guess or a reparsed fragment of the message. */}
          {serverVerdict && <p className="text-sm text-muted">{serverVerdict}</p>}
          <RadioGroup
            options={[
              ...REMAINDER_OPTIONS,
              ...(offerFee
                ? [
                    {
                      value: 'fee',
                      label: FEE_LABEL,
                      disabled: feeBlocked,
                      hint: feeBlocked ? 'Settle the linked payments separately to book a bank fee' : undefined,
                    },
                  ]
                : []),
            ]}
            value={feeMode ? 'fee' : remainder}
            onChange={(v) => chooseMode(v as Mode)}
          />
        </fieldset>
      )}

      <div>
        <p className="mb-1 text-xs font-semibold uppercase tracking-[0.06em] text-muted">Receipts</p>
        <ReceiptUploadButton
          target={{ expected_id: expectedId }}
          onUploaded={(id, file) => {
            setUploadError(null);
            setReceipts((rs) => [...rs, { id, name: file.name }]);
          }}
          onError={setUploadError}
        />
        <AttachmentList
          className="mt-2 flex flex-col gap-1"
          items={receipts}
          onRemove={(id) => setReceipts((rs) => rs.filter((r) => r.id !== id))}
        />
        {uploadError && (
          <p role="alert" className="mt-1 text-sm text-destructive">
            {uploadError}
          </p>
        )}
      </div>

      <DraftSuggestionInfo state={state} preview={suggestionQuery} />

      {problems.length > 0 && (
        <ul className="flex flex-col gap-0.5 text-sm text-[var(--ollio-gold-deep)]">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onClose} disabled={settle.isPending}>
          Cancel
        </Button>
        <Button type="submit" variant={twice.armed ? 'gold' : 'primary'} disabled={!canConfirm} loading={settle.isPending}>
          {twice.armed ? `Confirm — ${offPlanText(pressing)}` : 'Confirm'}
        </Button>
      </div>
    </form>
  );
}
